import { Link, useParams } from "react-router-dom";
import { ArrowLeft, EnvelopeSimple, FacebookLogo, InstagramLogo, XLogo, YoutubeLogo } from "@phosphor-icons/react";

import { StoryCard } from "@/components/StoryCard";
import { Button } from "@/components/ui/button";
import { usePageSeo } from "@/hooks/usePageSeo";
import { buildAuthorProfile } from "@/utils/newsroomStaticContent";

const socialIcons = {
  Instagram: InstagramLogo,
  YouTube: YoutubeLogo,
  Facebook: FacebookLogo,
  X: XLogo,
};

export const AuthorProfilePage = ({ feed }) => {
  const { authorSlug } = useParams();
  const authors = Array.from(new Set((feed || []).map((story) => story.author).filter(Boolean)));
  const authorName = authors.find((author) => author.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") === authorSlug) || "Content Watch Staff";
  const profile = buildAuthorProfile(authorName, feed);

  usePageSeo({
    canonicalPath: `/authors/${profile.slug}`,
    description: `${profile.name} reporter page on Content Watch HQ with biography, contact information, and story archive.`,
    title: `${profile.name} | Content Watch HQ`,
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="author-profile-page">
      <section className="border border-[#D9DCE1] bg-white p-8" data-testid="author-profile-hero">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#D21404]" data-testid="author-profile-overline">Reporter profile</p>
        <h1 className="mt-3 font-[Bebas_Neue] text-5xl uppercase tracking-[0.04em] text-[#0A0A0A] sm:text-6xl" data-testid="author-profile-name">{profile.name}</h1>
        <p className="mt-3 text-lg font-semibold uppercase tracking-[0.18em] text-[#1338BE]" data-testid="author-profile-role">{profile.role}</p>
        <p className="mt-5 max-w-3xl text-base leading-8 text-slate-700" data-testid="author-profile-bio">{profile.bio}</p>
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-700">
          <span className="inline-flex items-center gap-2" data-testid="author-profile-email">
            <EnvelopeSimple size={16} weight="bold" />
            {profile.email}
          </span>
          <span data-testid="author-profile-location">{profile.location}</span>
        </div>
        <div className="mt-6 flex flex-wrap gap-3" data-testid="author-profile-social-links">
          {profile.socialLinks.map((social) => (
            (() => {
              const Icon = socialIcons[social.label] || InstagramLogo;
              return (
                <a key={social.url} aria-label={social.label} className="inline-flex items-center justify-center border border-[#D9DCE1] p-3 text-slate-700 transition-colors hover:bg-[#1338BE] hover:text-white" data-testid={`author-profile-social-${social.label.toLowerCase()}`} href={social.url} rel="noreferrer" target="_blank">
                  <Icon size={18} weight="fill" />
                </a>
              );
            })()
          ))}
        </div>
        <div className="mt-6">
          <Button asChild className="rounded-none border border-[#0A0A0A] bg-transparent px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white" data-testid="author-profile-home-button" variant="ghost">
            <Link to="/">
              <ArrowLeft size={16} weight="bold" />
              Back Home
            </Link>
          </Button>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="author-profile-story-grid">
        {profile.stories.map((story) => <StoryCard key={story.id || story.slug} story={story} />)}
      </section>
    </main>
  );
};